export interface BatchedMessage {
  type: '__BATCH__';
  messages: any[];
}

export interface StatusMessage {
  type: '__STATUS__';
  status: 'CONNECTED' | 'DISCONNECTED';
}

export type SocketMessage = BatchedMessage | StatusMessage | any;

class SocketManager {
  ws: WebSocket | null = null;
  url: string | null = null;
  protocol: string | undefined = undefined;

  isConnected = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 10;

  baseDelay = 1000;
  maxDelay = 15000;

  heartbeatInterval: NodeJS.Timeout | null = null;
  heartbeatTime = 900000;
  lastPong = Date.now();

  messageQueue: any[] = [];
  flushScheduled = false;

  listeners = new Set<(data: SocketMessage) => void>();

  connect(url: string, protocol?: string) {
    this.url = url;
    this.protocol = protocol;

    if (this.ws) {
      this.cleanup();
    }

    this.ws = new WebSocket(url, protocol ? protocol : '') as WebSocket;

    this.ws.onopen = () => {
      console.log('WS Connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;

      this.startHeartbeat();
      this.listeners.forEach((cb) => cb({ type: '__STATUS__', status: 'CONNECTED' }));
    };

    this.ws.onmessage = (msg: MessageEvent) => {
      const data = JSON.parse(msg.data);

      if (data.type === '__PONG__') {
        this.lastPong = Date.now();
        return;
      }

      this.messageQueue.push(data);
      this.scheduleFlush();
    };

    this.ws.onerror = (error: Event) => {
      console.error('WS Error:', error);
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.warn('WS Disconnected', event);
      this.isConnected = false;

      this.listeners.forEach((cb) => cb({ type: '__STATUS__', status: 'DISCONNECTED' }));

      this.stopHeartbeat();
      this.scheduleReconnect();
    };
  }

  scheduleFlush() {
    if (this.flushScheduled) return;
    this.flushScheduled = true;

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => this.flushMessages());
    } else {
      setTimeout(() => this.flushMessages(), 16);
    }
  }

  flushMessages() {
    this.flushScheduled = false;

    if (this.messageQueue.length === 0) return;

    const messages = this.messageQueue;
    this.messageQueue = [];

    const batchMessage: BatchedMessage = { type: '__BATCH__', messages };
    this.listeners.forEach((cb) => cb(batchMessage));
  }

  send(data: any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('Socket not ready. Dropped:', data);
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached');
      return;
    }

    const delay = Math.min(this.baseDelay * Math.pow(2, this.reconnectAttempts), this.maxDelay);

    console.log(`Reconnecting in ${delay / 1000}s...`);

    setTimeout(() => {
      this.reconnectAttempts++;
      if (this.url) {
        this.connect(this.url, this.protocol);
      }
    }, delay);
  }

  startHeartbeat() {
    this.lastPong = Date.now();

    this.heartbeatInterval = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

      this.send({ type: '__PING__' });

      if (Date.now() - this.lastPong > this.heartbeatTime * 1.5) {
        console.warn('Server missed pong. Closing connection.');
        this.ws.close();
      }
    }, this.heartbeatTime);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
  }

  cleanup() {
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.close();
    }
    this.stopHeartbeat();
    this.messageQueue = [];
    this.flushScheduled = false;
  }

  subscribe(callback: (data: SocketMessage) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
}

export const socketManager = new SocketManager();
